import api from '~/plugins/apiClient';
import { ApiHandlerWithArgs, ApiHandlerWithData, ApiResponseContext } from '~/plugins/apiClient/_types';
import { FetchListOptions, PaginationMetaSchema } from '~/api/_types';
import {
    FetchOrdersResponse,
    FetchOrdersResponseSchema,
    OrderDetail,
    OrderDetailSchema,
    OrderDraftDetail,
    OrderDraftDetailSchema,
    OrderFormData,
} from './_types';
import { formatApiUrl } from '~/plugins/apiClient/utils';
import { transformZodSchemaFn } from '~/plugins/zod/utils';
import { transformFetchListOptionsToQuery } from '~/api/_transformers';
import { API_DATE_FORMAT } from '~/constants/_constants';
import { omit } from 'lodash-es';

const apiEndpointPrefix = 'orders';

export const fetchOrdersRequest: ApiHandlerWithArgs<
    FetchListOptions,
    ApiResponseContext<FetchOrdersResponse | null>
> = async (opt) => {
    const url = formatApiUrl(apiEndpointPrefix);
    return api.get(url, {
        transform: transformZodSchemaFn(FetchOrdersResponseSchema, url),
        transformMeta: transformZodSchemaFn(PaginationMetaSchema, url),
        params: transformFetchListOptionsToQuery(opt),
    });
};

export const fetchOrderDetailDraftRequest: ApiHandlerWithData<
    number,
    ApiResponseContext<OrderDraftDetail | null>
> = async (draftId) => {
    const url = formatApiUrl(apiEndpointPrefix, `${draftId}/draft`);
    return api.get(url, {
        transform: transformZodSchemaFn(OrderDraftDetailSchema, url),
    });
};

export const fetchOrderDetailRequest: ApiHandlerWithData<number, ApiResponseContext<OrderDetail>> = async (id) => {
    const url = formatApiUrl(apiEndpointPrefix, id);
    return api.get(url, { transform: transformZodSchemaFn(OrderDetailSchema, url) });
};

export const postOrderRequest: ApiHandlerWithData<
    OrderFormData,
    ApiResponseContext<OrderDetail | null, OrderFormData>
> = async (data) => {
    const url = formatApiUrl(apiEndpointPrefix, 'send');
    return api.post(url, transformOrderFormDataToRaw(data), {
        transform: transformZodSchemaFn(OrderDetailSchema, url),
    });
};

export const postOrderDraftRequest: ApiHandlerWithData<
    OrderFormData,
    ApiResponseContext<OrderDraftDetail | null, OrderFormData>
> = async (data) => {
    const url = formatApiUrl(apiEndpointPrefix, 'draft');
    return api.post(url, transformOrderFormDataToRaw(data), {
        transform: transformZodSchemaFn(OrderDraftDetailSchema, url),
    });
};

export const deleteOrderDraftRequest: ApiHandlerWithData<number, ApiResponseContext<null>> = async (id) => {
    const url = formatApiUrl(apiEndpointPrefix, `${id}/delete-draft`);
    return api.get(url);
};

export const revertOrderToDraftRequest: ApiHandlerWithData<number, ApiResponseContext<OrderDraftDetail>> = async (
    id
) => {
    const url = formatApiUrl(apiEndpointPrefix, `${id}/revert-to-draft`);
    return api.get(url);
};

const transformOrderFormDataToRaw = (formData: OrderFormData) => {
    return {
        Order: {
            ...formData.Order,
            PartnerId: formData.Order.PartnerId.Id,
            ContactId: formData.Order.ContactId?.Id ?? null,
            RequiredAt: formData.Order.RequiredAt?.toFormat(API_DATE_FORMAT) ?? null,
            AddressBillingId: formData.Order.AddressBillingId?.Id ?? null,
            AddressShippingId: formData.Order.AddressShippingId?.Id ?? null,
        },
        Lines: formData.Lines.map((x) => ({
            ...omit(x, ['Id', 'Item', 'CategoryId', 'PriceCatalogue', 'PriceDistributor', 'Nomenclature']),
            ItemId: x.Item.Id,
            RequiredAt: x.RequiredAt == null ? null : x.RequiredAt.toFormat(API_DATE_FORMAT),
        })),
    };
};
